import React, { useContext, useState } from "react";
import { Card, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as MoreSVG } from "../../../../../../../../../../images/svg/documents/more.svg";
import { TransferItemApi } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import { DocumentItemsFilter } from "../../../../components/DocumentItemsFilter";
import { getProductVolumeLabel } from "../../../../utils/itemsTableUtils";
import { TransferContext } from "../../pages/Preview";

const TransferItemsCard = () => {
	const { t } = useTranslation();
	const { transfer, onFetchTransfer } = useContext(TransferContext);
	const [filteredTransferItems, setFilteredTransferItems] = useState<TransferItemApi[]>(transfer.items);
	const organization = useSelector(selectOrganization);
	const [shownIndexes, setShownIndexes] = useState<number[]>([]);
	const confirmation = useConfirmation();
	const { addSuccessFlash, addFlash } = useFlash();
	const isActionColumnShown = transfer.items.some((item) => item?.product_id);

	const drawCost = (cost?: MoneyApi) => {
		let result = "-";
		if (!isNaN(Number(cost?.amount))) result = FormatMoney(cost);
		return result;
	};

	const isRowIndexShown = (index: number) => {
		return shownIndexes.includes(index);
	};

	const onShowMore = (index: number) => {
		if (isRowIndexShown(index)) {
			const indexes = shownIndexes;
			indexes.splice(shownIndexes.indexOf(index), 1);
			setShownIndexes([...indexes]);
		} else {
			setShownIndexes((indexes) => [...indexes, index]);
		}
	};

	const handleCorrection = async (item: TransferItemApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.action", { ns: "lib" }),
			});
			await api.organization().correctProductRecipe(item.product_id);

			onFetchTransfer();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<Card className="card-box positions-card">
			<Card.Body>
				<h5 className="card-title">{t("common.word.items")}</h5>
				<div className="table-responsive">
					<table className="table table-preview">
						<thead>
							<tr>
								<th className="action" />
								<th>{t("lib:common.word.name")}</th>
								<th>{t("common.word.volume")}</th>
								<th>{t("common.word.quantity")}</th>
								<th>{t("common.word.total_volume")}</th>
								<th>{t("common.word.item_cost")}</th>
								<th>{t("common.word.total_cost")}</th>
								{isActionColumnShown && <th className="action" />}
							</tr>
						</thead>
						<tbody>
							<DocumentItemsFilter
								numberOfColumns={8}
								items={transfer.items}
								numberOfFilteredItems={filteredTransferItems.length}
								setItems={setFilteredTransferItems}
							/>
							{filteredTransferItems.map((item, index) => {
								const indexInOriginalItemsArray = transfer.items.findIndex(
									(originalItem) => originalItem.id === item.id
								);
								const measureType = item?.product?.measure_type;

								return (
									<React.Fragment key={item.id}>
										<tr>
											<td className="action">
												{item?.items?.length > 0 && (
													<DropdownSVG
														className="icon"
														id={`icon-${index}`}
														style={{
															transform: isRowIndexShown(index)
																? "rotate(180deg)"
																: "unset",
														}}
														onClick={() => onShowMore(index)}
													/>
												)}
											</td>
											<td className="w-40">
												<div className="d-flex flex-column">
													<div className="d-flex align-items-center column-gap-2">
														<span>{`${indexInOriginalItemsArray + 1}.`}</span>
														<Link
															rel={"noreferrer"}
															target={"_blank"}
															to={`/${organization.id}/products/${item.product.id}/edit`}
														>
															{item.product.name}
															{getProductVolumeLabel(item.product, item)}
														</Link>
													</div>
													{item?.comment && (
														<small className="text-dark">{item.comment}</small>
													)}
												</div>
											</td>
											<td>
												{item.volume.toFixed(5)}{" "}
												{measureType ? t(`enums.products.measure_types.${measureType}`) : ""}
											</td>
											<td>{parseFloat(item.quantity.toFixed(5))}</td>
											<td>
												{(item.quantity * item.volume).toFixed(5)}{" "}
												{measureType ? t(`enums.products.measure_types.${measureType}`) : ""}
											</td>
											<td>{drawCost(item?.unit_fifo_cost_net_money)}</td>
											<td>{drawCost(item?.total_fifo_cost_net_money)}</td>
											{item?.product_id && (
												<td className="action">
													<Dropdown className="icon">
														<Dropdown.Toggle as={MoreSVG} />
														<Dropdown.Menu>
															{item?.product_id && (
																<Dropdown.Item onClick={() => handleCorrection(item)}>
																	{t("common.action.recipe_correction")}
																</Dropdown.Item>
															)}
														</Dropdown.Menu>
													</Dropdown>
												</td>
											)}
										</tr>
										{isRowIndexShown(index) &&
											item.items.map((subItem, i) => (
												<tr key={`transfer-item-${index}-item-${i}`}>
													<td />
													<td>
														<Link
															rel={"noreferrer"}
															target={"_blank"}
															to={`/${organization.id}/products/${subItem.product.id}/edit`}
															className="ms-4"
														>
															{subItem.product.name}
														</Link>
													</td>
													<td />
													<td>{subItem.quantity.toFixed(5)}</td>
													<td />
													<td>{drawCost(subItem?.unit_fifo_cost_net_money)}</td>
													<td colSpan={isActionColumnShown ? 2 : 1}>
														{drawCost(subItem?.total_fifo_cost_net_money)}
													</td>
												</tr>
											))}
									</React.Fragment>
								);
							})}
							<tr className="single-summary-row">
								<td colSpan={5} />
								<td>{t("common.word.total_cost")}</td>
								<td colSpan={isActionColumnShown ? 3 : 2} className="text-end">
									{drawCost(transfer?.total_fifo_cost_net_money)}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</Card.Body>
		</Card>
	);
};

export default TransferItemsCard;
